import React, { useState, useEffect, useMemo } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { FirmDatabase } from '../../database/firmDatabase';
import { telemetry } from '@/lib/telemetry';
import { MerchantAccount } from '../../database/baseTypes';

interface AccountAutocompleteProps {
  firmDb?: FirmDatabase;
  value?: string;
  error: boolean;
  helperText?: string;
  inputRef?: React.Ref<any>;
  label?: string;
  required?: boolean;
  operatingOrTrust?: MerchantAccount['operatingOrTrust'];
  isSandbox?: boolean;
  onSelect: (account?: string) => void;
}

const pinkFocusStyles = {
  '& .MuiOutlinedInput-root': {
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#ff00bf',
    },
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#ff00bf',
  },
};

const AccountAutocomplete = ({
  firmDb,
  value,
  error,
  helperText,
  inputRef,
  label = 'Account',
  required = true,
  operatingOrTrust,
  isSandbox,
  onSelect,
}: AccountAutocompleteProps) => {
  const [options, setOptions] = useState<readonly MerchantAccount[]>([]);
  const selectedAccount = useMemo(
    () => options.find((opt) => opt.id === value) || null,
    [options, value],
  );

  useEffect(() => {
    const fetchAccounts = async () => {
      if (!firmDb) {
        setOptions([]);
        return;
      }

      try {
        const firmDoc = await firmDb.getFirm();
        const accounts = (firmDoc.data()?.merchantAccounts || []).filter(
          (account) =>
            (!operatingOrTrust ||
              account.operatingOrTrust === operatingOrTrust) &&
            (isSandbox === undefined || account.isSandbox === isSandbox),
        );
        setOptions(accounts);
      } catch (error) {
        console.error('Error fetching accounts: ', error);
        telemetry.error({
          error,
          message: 'Error fetching accounts',
        });
        setOptions([]);
      }
    };

    fetchAccounts();
  }, [firmDb, isSandbox, operatingOrTrust]);

  return (
    <Autocomplete
      options={options}
      noOptionsText='No accounts'
      onChange={(_, newValue) => onSelect(newValue?.id ?? undefined)}
      value={selectedAccount}
      getOptionLabel={(option) => option.accountNickname}
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={inputRef}
          label={label}
          InputLabelProps={{
            ...params.InputLabelProps,
            ...(selectedAccount ? { shrink: true } : {}),
          }}
          fullWidth
          error={error}
          helperText={helperText}
          required={required}
          sx={pinkFocusStyles}
        />
      )}
    />
  );
};

export default AccountAutocomplete;
